import React, { useMemo } from 'react';

import decodeHTML from '@exp/utilities/decodeHTML';
import { ExpLabel, getExpMessage } from '@exp/components/localization';

import { AffiliationStatus, ApplicationStatus } from '../constants/constants';
import { ModeConfig } from '../constants/modes';
import { selectConfig } from '../redux/context';
import { useAppSelector } from '../redux/hooks';
import type { ParsedAffiliation } from '../types/Affiliation';
import { getApplicationDisplayStatus, isApplicationPending } from '../utilities/statuses';
import { useSendEvent } from '../utilities/useAnalytics';
import {
  getApplicationStatusUrl,
  getApplicationUrl,
  getCommunityPageUrl,
  hasRenewalPath,
  isRecentExpiredApplication,
} from '../utilities/utilities';
import Stepper from './Stepper';

const AnalyticEvents = {
  CLICK_AFFILIATION: 'ONBOARDING_CLICK_MY_TEAMS',
};

type AffiliationCardProps = {
  affiliation: ParsedAffiliation;
};

const AffiliationCard = ({ affiliation }: AffiliationCardProps) => {
  const config: ModeConfig = useAppSelector(selectConfig);
  const sendEvent = useSendEvent({ mode: config.mode });

  // latestApplicationStatus is typed `string | undefined` on ParsedAffiliation, but the status
  // helpers take the ApplicationStatus enum — the underlying values are the same backend strings.
  const applicationStatus = affiliation.latestApplicationStatus as ApplicationStatus;
  const { expirationDate } = affiliation.affiliation ?? {};

  const displayStatus = isApplicationPending(applicationStatus)
    ? getApplicationDisplayStatus(applicationStatus)
    : affiliation.affiliationStatus || getApplicationDisplayStatus(applicationStatus);

  let message = getExpMessage({ messageKey: `onb.affiliationsList.cta.${displayStatus}`, defaultMessage: '' });
  // Differentiate between expired application and affiliation — only fall back to the generic
  // affiliation-expired message when there isn't also a *recent* (<30 day) expired application
  // driving it (e.g. an expired renewal), which should keep the application-specific message.
  // Once that application ages out of the window, the reapply messaging no longer applies.
  if (displayStatus === AffiliationStatus.EXPIRED && affiliation.expired && !isRecentExpiredApplication(affiliation)) {
    message = getExpMessage({ messageKey: 'onb.affiliationsList.cta.EXPIRED_AFF', defaultMessage: 'Expired' });
  }
  // Active shows expiration time
  if (affiliation.expiringSoon) {
    const diffTime = new Date(expirationDate ?? '').getTime() - Date.now();
    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    message = expirationDate
      ? getExpMessage({
          messageKey: 'onb.affiliationsList.cta.expiringSoonWhen',
          defaultMessage: '{days} days left to renew',
          values: { days },
        })
      : '';
  } else if (displayStatus === AffiliationStatus.ACTIVE && expirationDate) {
    const expires = new Date(expirationDate);
    const date = expires.toLocaleDateString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    message =
      expires.getFullYear() < 2099
        ? getExpMessage({
            messageKey: 'onb.affiliationsList.cta.expiringLaterWhen',
            defaultMessage: 'Expires on {date}',
            values: { date },
          })
        : '';
  }

  const destinationUrl = useMemo(() => {
    if (isApplicationPending(applicationStatus)) {
      return getApplicationStatusUrl(affiliation.teamId);
    }
    if (getCommunityPageUrl(affiliation.team)) {
      return getCommunityPageUrl(affiliation.team);
    }
    if (affiliation.active) {
      return null;
    }
    return getApplicationUrl(affiliation.teamId, affiliation.teamKey);
  }, [affiliation, applicationStatus]);

  const Tag: React.ElementType = destinationUrl ? 'a' : 'div';

  return (
    <div
      className={`affiliation-item ${displayStatus}`}
      data-team={affiliation.teamId}
      key={affiliation.teamId}
      data-linked={!!destinationUrl}
    >
      <Tag
        className="affiliation-item-contents"
        href={destinationUrl}
        onClick={() => {
          sendEvent(AnalyticEvents.CLICK_AFFILIATION, {
            teamId: affiliation.teamId,
            status: displayStatus,
            hasTeamPage: !!getCommunityPageUrl(affiliation.team),
          });
        }}
      >
        <div className="affiliations-item-header">
          <p className="affiliation-name">{decodeHTML(affiliation?.team?.name)}</p>
          <div className="attention-pills">
            {displayStatus === ApplicationStatus.IN_PROCESS && (
              <ExpLabel
                className="message-pill"
                defaultMessage="New"
                messageKey="onb.affiliationsList.newPill"
                tagName="p"
              />
            )}
          </div>
          {destinationUrl && <i className="exp-ux-small exp-ux-chevron" />}
        </div>
        <p className="affiliation-cta">{message}</p>
        {affiliation.pending ? <Stepper active prefill segmentsComplete={1} segments={2} /> : null}
        {affiliation.draft ? <Stepper active prefill segmentsComplete={0} segments={2} /> : null}
        {/* An expiring-soon affiliation with no renewal path still shows the card, but there's no
            action to take, so the "Renew Verification" pill is hidden. `renewable` is the wrong
            test here — it also counts a public application form, which is a new application
            rather than a renewal and leaves the pill pointing nowhere. */}
        {(displayStatus !== AffiliationStatus.EXPIRING_SOON || hasRenewalPath(affiliation.team)) && (
          <div className={`status-pill ${displayStatus || 'unknown'} ${affiliation.active ? 'active' : ''}`}>
            <ExpLabel
              className="message-pill"
              defaultMessage={displayStatus}
              messageKey={`onb.affiliationsList.statusPill.${displayStatus || 'unknown'}`}
            />
          </div>
        )}
      </Tag>
    </div>
  );
};

AffiliationCard.displayName = 'AffiliationCard';

export default AffiliationCard;
